import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getUsers, updateUser, deleteUser, register, depositVacationDays, getUserVacationBalance, adminFlexAdjustment, getUserFlexTransactions, changeUserRole, getMainCostCodes, getFlexBalance } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Palmtree, Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { getCappedHours } from '../../lib/format-utils';
import { getApiErrorMessage } from '../../lib/errors';

export default function EmployeesPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [mainCostCodes, setMainCostCodes] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
    const [flexDialogOpen, setFlexDialogOpen] = useState(false);
    const [flexLimits, setFlexLimits] = useState({ min: -40, max: 80 });
    const [editingUser, setEditingUser] = useState(null);
    const [selectedUserVacation, setSelectedUserVacation] = useState(null);
    const [selectedUserFlex, setSelectedUserFlex] = useState(null);
    const [flexTransactions, setFlexTransactions] = useState([]);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        role: 'employee',
        is_active: true,
        monthly_salary: 0,
        mileage_rate: 18.5,
        vacation_days: 0,
        advance_vacation_days: 10,
        employment_start_date: '',
        daily_base_hours: '',  // empty string = inherit company default (null on save)
        employer_tax_class: 'standard',
        manager_id: '',
        cost_code_id: '',
    });
    const [vacationDeposit, setVacationDeposit] = useState({ days: 25, reason: 'Annual vacation days deposit' });
    const [flexAdjustment, setFlexAdjustment] = useState({ hours: 0, reason: '' });
    const [saving, setSaving] = useState(false);

    const fetchUsers = useCallback(async () => {
      try {
        const [usersRes, costCodesRes] = await Promise.all([
          getUsers(true),
          getMainCostCodes().catch(() => ({ data: [] })),
        ]);
        setUsers(usersRes.data);
        setMainCostCodes(costCodesRes.data || []);
      } catch (error) {
        toast.error(t('employees.failed_load'));
      } finally {
        setLoading(false);
      }
    }, [t]);

    useEffect(() => {
      fetchUsers();
    }, [fetchUsers]);



    const openCreateDialog = () => {
        setEditingUser(null);
        setFormData({
            email: '',
            name: '',
            password: '',
            role: 'employee',
            is_active: true,
            monthly_salary: 0,
            mileage_rate: 18.5,
            vacation_days: 0,
            advance_vacation_days: 10,
            employment_start_date: new Date().toISOString().split('T')[0],
            daily_base_hours: '',
            employer_tax_class: 'standard',
            manager_id: '',
            cost_code_id: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (user) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            name: user.name,
            password: '',
            role: user.role,
            is_active: user.is_active ?? true,
            monthly_salary: user.monthly_salary ?? 0,
            mileage_rate: user.mileage_rate || 18.5,
            vacation_days: user.vacation_days || 0,
            advance_vacation_days: user.advance_vacation_days || 10,
            employment_start_date: user.employment_start_date || '',
            daily_base_hours: user.daily_base_hours ?? '',
            employer_tax_class: user.employer_tax_class || 'standard',
            manager_id: user.manager_id || '',
            cost_code_id: user.cost_code_id || '',
        });
        setDialogOpen(true);
    };

    const openVacationDialog = async (user) => {
        try {
            const res = await getUserVacationBalance(user.id);
            setSelectedUserVacation({ ...user, balance: res.data });
            setVacationDeposit({ days: 25, reason: 'Annual vacation days deposit' });
            setVacationDialogOpen(true);
        } catch (error) {
            toast.error(t('employees.failed_load_vacation'));
        }
    };

    const openFlexDialog = async (user) => {
        try {
            const [res, flexBalRes] = await Promise.all([
                getUserFlexTransactions(user.id),
                getFlexBalance().catch(() => null),
            ]);
            setSelectedUserFlex(user);
            setFlexTransactions(res.data || []);
            setFlexAdjustment({ hours: 0, reason: '' });
            if (flexBalRes?.data) {
                setFlexLimits({
                    min: flexBalRes.data.flex_limit_min ?? -40,
                    max: flexBalRes.data.flex_limit_max ?? 80,
                });
            }
            setFlexDialogOpen(true);
        } catch (error) {
            toast.error(t('employees.failed_flex_load'));
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error(t('employees.name_required'));
            return;
        }

        setSaving(true);
        try {
            if (editingUser) {
              // 1) Role change must go through /users/{id}/role
              if (formData.role !== editingUser.role) {
                const isDemotingLastAdmin =
                  editingUser.role === 'admin' &&
                  formData.role === 'employee' &&
                  adminCount <= 1;

                if (isDemotingLastAdmin) {
                  toast.error(t('employees.cant_demote_last_admin'));
                  setSaving(false);
                  return;
                }

                await changeUserRole(editingUser.id, formData.role);
              }

              // 2) Everything else can go through /users/{id}
              await updateUser(editingUser.id, {
                name: formData.name,
                monthly_salary: parseFloat(formData.monthly_salary || 0),
                mileage_rate: parseFloat(formData.mileage_rate),
                vacation_days: parseFloat(formData.vacation_days),
                advance_vacation_days: parseFloat(formData.advance_vacation_days),
                employment_start_date: formData.employment_start_date,
                is_active: !!formData.is_active,
                daily_base_hours: formData.daily_base_hours !== '' && formData.daily_base_hours !== null
                    ? parseFloat(formData.daily_base_hours)
                    : null,
                employer_tax_class: formData.employer_tax_class || 'standard',
                manager_id: formData.manager_id || null,
                cost_code_id: formData.cost_code_id || null,
              });

              toast.success(t('employees.user_updated'));
            }
 else {
                if (!formData.email || !formData.password) {
                    toast.error(t('employees.email_password_required'));
                    setSaving(false);
                    return;
                }
                const createRes = await register({
                    email: formData.email,
                    name: formData.name,
                    password: formData.password,
                    role: formData.role,
                    is_active: !!formData.is_active,
                    monthly_salary: parseFloat(formData.monthly_salary || 0)
                });
                // Set manager_id on the newly-created user if one was selected
                if (formData.manager_id && createRes?.data?.user?.id) {
                    await updateUser(createRes.data.user.id, { manager_id: formData.manager_id });
                }
                toast.success(t('employees.user_created'));
            }
            setDialogOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('employees.failed_save'));
        } finally {
            setSaving(false);
        }
    };

    const handleDepositVacation = async () => {
        if (!vacationDeposit.days || vacationDeposit.days <= 0) {
            toast.error(t('employees.invalid_days'));
            return;
        }

        setSaving(true);
        try {
            await depositVacationDays({
                user_id: selectedUserVacation.id,
                days: parseFloat(vacationDeposit.days),
                reason: vacationDeposit.reason
            });
            toast.success(t('employees.deposit_success', { days: vacationDeposit.days }));
            setVacationDialogOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('employees.failed_deposit'));
        } finally {
            setSaving(false);
        }
    };

    const handleFlexAdjustment = async () => {
        if (!flexAdjustment.hours || flexAdjustment.hours === 0) {
            toast.error(t('employees.enter_hours'));
            return;
        }
        if (!flexAdjustment.reason.trim()) {
            toast.error(t('employees.provide_reason'));
            return;
        }

        setSaving(true);
        try {
            const res = await adminFlexAdjustment({
                user_id: selectedUserFlex.id,
                hours: parseFloat(flexAdjustment.hours),
                reason: flexAdjustment.reason
            });
            toast.success(t('employees.flex_adjusted', { balance: res.data.new_balance }));
            setFlexDialogOpen(false);
            fetchUsers();
        } catch (error) {
          toast.error(getApiErrorMessage(error, t('employees.failed_flex_adjust')));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (user) => {
        if (!window.confirm(t('employees.delete_confirm', { name: user.name }))) return;

        try {
            await deleteUser(user.id);
            toast.success(t('employees.user_deleted'));
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('employees.failed_delete'));
        }
    };
    const adminCount = users.filter(u => u.role === 'admin').length;
    const handleActiveToggle = async (user) => {
      const currentActive = user.is_active ?? true;
      const newActive = !currentActive;
      const actionText = newActive ? t('employees.activate_confirm', { name: user.name }) : t('employees.deactivate_confirm', { name: user.name });

      if (!window.confirm(actionText)) return;

      try {
        await updateUser(user.id, { is_active: newActive });
        toast.success(newActive ? t('employees.now_active', { name: user.name }) : t('employees.now_inactive', { name: user.name }));
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('employees.failed_update_status'));
      }
    };
    const roleChipClass = (role) => {
      switch (role) {
        case 'super_admin': return 'bg-purple-100 text-purple-900';
        case 'owner':       return 'bg-amber-100 text-amber-900';
        case 'admin':       return 'bg-foreground text-background';
        case 'accountant':  return 'bg-blue-100 text-blue-900';
        default:            return 'bg-muted text-foreground';
      }
    };

    const roleLabelFallback = (role) =>
      (role || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }
const activeUsers = users.filter(u => (u.is_active ?? true) === true);
const inactiveUsers = users.filter(u => (u.is_active ?? true) === false);

    return (
        <div className="space-y-6" data-testid="employees-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('employees.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('employees.subtitle')}</p>
                </div>
                <Button
                    className="uppercase tracking-wider"
                    onClick={openCreateDialog}
                    data-testid="add-employee-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('employees.add_employee')}
                </Button>
            </div>

            {/* Users List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {users.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{t('employees.no_users')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('employees.col_name')}</th>
                                    <th>{t('employees.col_email')}</th>
                                    <th>{t('employees.col_role')}</th>
                                    <th>{t('employees.col_status')}</th>
                                    <th>{t('employees.col_monthly_salary')}</th>
                                    <th>{t('users.cost_code_label')}</th>
                                    <th>{t('employees.col_flex')}</th>
                                    <th>{t('employees.col_vacation')}</th>
                                    <th className="text-right">{t('employees.col_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td className="font-medium">{user.name}</td>
                                        <td className="font-mono text-sm">{user.email}</td>
                                        <td>
                                            <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${roleChipClass(user.role)}`}>
                                                {t(`employees.role_${user.role}`, roleLabelFallback(user.role))}
                                            </span>
                                        </td>
                                        <td>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleActiveToggle(user)}
                                              className={(user.is_active ?? true) ? 'text-green-600 hover:text-green-700' : 'text-muted-foreground hover:text-foreground'}
                                              title={(user.is_active ?? true) ? t('employees.deactivate_employee') : t('employees.activate_employee')}
                                            >
                                              {(user.is_active ?? true) ? t('employees.status_active') : t('employees.status_inactive')}
                                            </Button>
                                        </td>

                                        <td className="font-mono">
                                            {user.monthly_salary ?? 0} SEK
                                            {user.employer_tax_class && user.employer_tax_class !== 'standard' && (
                                                <span className="ml-2 inline-block px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-200">
                                                    10.21%
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-xs text-muted-foreground">
                                            {user.cost_code_id
                                                ? (() => {
                                                    const cc = mainCostCodes.find(c => c.id === user.cost_code_id);
                                                    return cc
                                                        ? <span className="font-mono font-semibold">{cc.code} — {cc.name}</span>
                                                        : <span className="text-amber-600">{t('users.cost_code_none')}</span>;
                                                })()
                                                : <span>{t('users.cost_code_none')}</span>
                                            }
                                        </td>
                                        <td className={`font-mono font-bold ${(user.flex_balance || 0) >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openFlexDialog(user)}
                                                className={(user.flex_balance || 0) >= 0 ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'}
                                                data-testid={`flex-user-${user.id}`}
                                            >
                                                <Clock className="w-4 h-4 mr-1" />
                                                {(user.flex_balance || 0) > 0 ? '+' : ''}{user.flex_balance || 0}h
                                            </Button>
                                        </td>
                                        <td className="font-mono">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openVacationDialog(user)}
                                                className="text-green-600 hover:text-green-700"
                                                data-testid={`vacation-user-${user.id}`}
                                            >
                                                <Palmtree className="w-4 h-4 mr-1" />
                                                {user.vacation_days || 0}d
                                            </Button>
                                        </td>
                                        <td className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditDialog(user)}
                                                data-testid={`edit-user-${user.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(user)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`delete-user-${user.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </CardContent>
            </Card>
            {inactiveUsers.length > 0 && (
                <Card className="border border-border shadow-none">
                    <button
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                        onClick={() => setShowInactive(v => !v)}
                    >
                        <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                            {t('employees.inactive_employees', { count: inactiveUsers.length })}
                        </span>
                        {showInactive
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        }
                    </button>
                    {showInactive && (
                        <CardContent className="p-0 border-t border-border">
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('employees.col_name')}</th>
                                            <th>{t('employees.col_email')}</th>
                                            <th>{t('employees.col_role')}</th>
                                            <th>{t('employees.col_status')}</th>
                                            <th>{t('employees.col_monthly_salary')}</th>
                                            <th>{t('users.cost_code_label')}</th>
                                            <th>{t('employees.col_flex')}</th>
                                            <th>{t('employees.col_vacation')}</th>
                                            <th className="text-right">{t('employees.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inactiveUsers.map((user) => (
                                            <tr key={user.id} className="opacity-60">
                                                <td className="font-medium">{user.name}</td>
                                                <td className="font-mono text-sm">{user.email}</td>

                                                <td>
                                                    <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${roleChipClass(user.role)}`}>
                                                        {t(`employees.role_${user.role}`, roleLabelFallback(user.role))}
                                                    </span>
                                                </td>

                                                <td>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleActiveToggle(user)}
                                                        className="text-muted-foreground hover:text-foreground"
                                                        title={t('employees.activate_employee')}
                                                    >
                                                        {t('employees.inactive_label')}
                                                    </Button>
                                                </td>

                                                <td className="font-mono">
                                                    {user.monthly_salary ?? 0} SEK
                                                    {user.employer_tax_class && user.employer_tax_class !== 'standard' && (
                                                        <span className="ml-2 inline-block px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-200">
                                                            10.21%
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-xs text-muted-foreground">
                                                    {user.cost_code_id
                                                        ? (() => {
                                                            const cc = mainCostCodes.find(c => c.id === user.cost_code_id);
                                                            return cc
                                                                ? <span className="font-mono">{cc.code}</span>
                                                                : <span>—</span>;
                                                        })()
                                                        : '—'
                                                    }
                                                </td>
                                                <td className="font-mono font-bold">
                                                    {(user.flex_balance || 0) > 0 ? '+' : ''}{user.flex_balance || 0}h
                                                </td>
                                                <td className="font-mono">{user.vacation_days || 0}d</td>

                                                <td className="text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(user)}
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}


            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingUser ? t('employees.edit_employee') : t('employees.new_employee')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                        {!editingUser && (
                            <>
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('employees.label_email')}</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder={t('employees.placeholder_email')}
                                        data-testid="employee-email-input"
                                    />
                                </div>
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('employees.label_password')}</Label>
                                    <Input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder={t('employees.placeholder_password')}
                                        data-testid="employee-password-input"
                                    />
                                </div>
                            </>
                        )}

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.label_name')}</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('employees.placeholder_name')}
                                data-testid="employee-name-input"
                            />
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.label_role')}</Label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid="employee-role-select"
                            >
                                <option value="employee">{t('employees.role_employee')}</option>
                                <option value="admin">{t('employees.role_admin')}</option>
                                <option value="accountant">{t('employees.role_accountant')}</option>
                                <option value="owner">{t('employees.role_owner')}</option>
                            </select>
                            {formData.role === 'owner' && (
                                <p className="text-xs text-amber-600 mt-1">
                                    {t('employees.role_owner_warning')}
                                </p>
                            )}
                        </div>

                        {/* Responsible Manager — available on both create and edit */}
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.label_manager')}</Label>
                            <select
                                value={formData.manager_id}
                                onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid="employee-manager-select"
                            >
                                <option value="">{t('employees.manager_none')}</option>
                                {users
                                    .filter(u => ['admin', 'owner', 'super_admin'].includes(u.role) && (u.is_active ?? true))
                                    .map(u => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))
                                }
                            </select>
                        </div>

                        {/* Cost code (main function) — salary cost allocation bucket for Phase C analytics */}
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('users.cost_code_label')}</Label>
                            <select
                                value={formData.cost_code_id}
                                onChange={(e) => setFormData({ ...formData, cost_code_id: e.target.value })}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid="employee-cost-code-select"
                            >
                                <option value="">{t('users.cost_code_placeholder')}</option>
                                {mainCostCodes
                                    .filter(cc => cc.active || cc.id === formData.cost_code_id)
                                    .map(cc => (
                                        <option key={cc.id} value={cc.id}>
                                            {cc.code} — {cc.name}
                                            {!cc.active ? ` (${t('common.inactive')})` : ''}
                                        </option>
                                    ))
                                }
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">{t('users.cost_code_none')}</p>
                        </div>

                        <div>
                          <Label className="label-uppercase mb-2 block">{t('employees.label_status')}</Label>
                          <select
                            value={formData.is_active ? 'active' : 'inactive'}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                            className="w-full h-10 px-3 border border-input bg-background text-sm"
                          >
                            <option value="active">{t('employees.status_active')}</option>
                            <option value="inactive">{t('employees.status_inactive')}</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="label-uppercase mb-2 block">{t('employees.label_monthly_salary')}</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={formData.monthly_salary}
                                    onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
                                    placeholder={t('employees.placeholder_monthly_salary')}
                                    data-testid="employee-rate-input"
                                />
                            </div>
                            <div>
                                <Label className="label-uppercase mb-2 block">{t('employees.label_mileage_rate')}</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={formData.mileage_rate}
                                    onChange={(e) => setFormData({ ...formData, mileage_rate: e.target.value })}
                                    placeholder={t('employees.placeholder_mileage_rate')}
                                    data-testid="employee-mileage-rate-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.tax_class_label')}</Label>
                            <select
                                value={formData.employer_tax_class}
                                onChange={(e) => setFormData({ ...formData, employer_tax_class: e.target.value })}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid="employee-tax-class-select"
                            >
                                <option value="standard">{t('employees.tax_class_standard')}</option>
                                <option value="reduced_young">{t('employees.tax_class_reduced_young')}</option>
                                <option value="reduced_old">{t('employees.tax_class_reduced_old')}</option>
                            </select>
                        </div>

                        {editingUser && (
                            <>
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('employees.label_employment_start')}</Label>
                                    <Input
                                        type="date"
                                        value={formData.employment_start_date}
                                        onChange={(e) => setFormData({ ...formData, employment_start_date: e.target.value })}
                                        data-testid="employee-start-date-input"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="daily-base-hours">{t('employees.daily_base_hours_label')}</Label>
                                    <Input
                                        id="daily-base-hours"
                                        type="number"
                                        step="0.5"
                                        min="1"
                                        max="24"
                                        placeholder={t('employees.daily_base_hours_placeholder')}
                                        value={formData.daily_base_hours}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            daily_base_hours: e.target.value
                                        }))}
                                        data-testid="employee-daily-hours-input"
                                    />
                                    <p className="text-xs text-muted-foreground">{t('employees.daily_base_hours_hint')}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="label-uppercase mb-2 block">{t('employees.label_vacation_earned')}</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={formData.vacation_days}
                                            onChange={(e) => setFormData({ ...formData, vacation_days: e.target.value })}
                                            placeholder="0"
                                            data-testid="employee-vacation-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="label-uppercase mb-2 block">{t('employees.label_advance_vacation')}</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="1"
                                            value={formData.advance_vacation_days}
                                            onChange={(e) => setFormData({ ...formData, advance_vacation_days: e.target.value })}
                                            placeholder="10"
                                            data-testid="employee-advance-vacation-input"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-employee-btn">
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Vacation Deposit Dialog */}
            <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Palmtree className="w-5 h-5" />
                            {t('employees.vacation_title', { name: selectedUserVacation?.name })}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Current Balance */}
                        <div className="grid grid-cols-2 gap-4 p-4 bg-muted">
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('employees.earned_days')}</p>
                                <p className="font-mono text-2xl font-bold flex-positive">
                                    {selectedUserVacation?.balance?.vacation_days || 0}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('employees.advance_available')}</p>
                                <p className="font-mono text-2xl font-bold">
                                    {(selectedUserVacation?.balance?.advance_vacation_days || 0) - (selectedUserVacation?.balance?.advance_vacation_used || 0)}
                                </p>
                            </div>
                        </div>

                        <div className="p-3 bg-green-50 border border-green-200 text-sm">
                            <p>
                                <strong>{t('employees.total_available')}</strong> {selectedUserVacation?.balance?.total_available || 0} days
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('employees.years_employed', { years: selectedUserVacation?.balance?.years_employed || 0 })}
                            </p>
                        </div>

                        <hr className="border-border" />

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.deposit_days_label')}</Label>
                            <Input
                                type="number"
                                min="0"
                                step="1"
                                value={vacationDeposit.days}
                                onChange={(e) => setVacationDeposit({ ...vacationDeposit, days: e.target.value })}
                                placeholder={t('employees.deposit_days_placeholder')}
                                data-testid="vacation-deposit-days-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('employees.deposit_standard')}
                            </p>
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('employees.deposit_reason_label')}</Label>
                            <Input
                                value={vacationDeposit.reason}
                                onChange={(e) => setVacationDeposit({ ...vacationDeposit, reason: e.target.value })}
                                placeholder={t('employees.deposit_placeholder')}
                                data-testid="vacation-deposit-reason-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVacationDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleDepositVacation} disabled={saving} data-testid="deposit-vacation-btn">
                            {saving ? t('common.depositing') : t('employees.deposit_days_btn')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Flex Adjustment Dialog */}
            <Dialog open={flexDialogOpen} onOpenChange={setFlexDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            {t('employees.flex_title', { name: selectedUserFlex?.name })}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Current Balance */}
                        <div className="p-4 bg-muted text-center">
                            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{t('employees.flex_current_balance')}</p>
                            <p className={`font-mono text-3xl font-bold ${(selectedUserFlex?.flex_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(selectedUserFlex?.flex_balance || 0) > 0 ? '+' : ''}{selectedUserFlex?.flex_balance || 0}h
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">{t('employees.flex_limit', { max: flexLimits.max ?? 80, min: flexLimits.min ?? -40 })}</p>
                        </div>

                        {/* Recent Transactions */}
                        {flexTransactions.length > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">{t('employees.recent_transactions')}</p>
                                <div className="max-h-36 overflow-y-auto border border-border">
                                    {flexTransactions.slice(0, 5).map((tx, idx) => {
                                        const capped = getCappedHours(tx);
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-2 border-b border-border last:border-b-0 text-sm gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <p className="text-xs text-muted-foreground">{tx.date}</p>
                                                        {capped ? (
                                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/60 px-1 py-0.5 rounded">
                                                                <AlertCircle className="w-2.5 h-2.5 shrink-0" />
                                                                {t('employees.flex_not_added', { count: capped })}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <p className="text-xs truncate max-w-[220px]" title={tx.reason}>{tx.reason}</p>
                                                </div>
                                                <span className={`font-mono font-bold shrink-0 ${tx.hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {tx.hours > 0 ? '+' : ''}{tx.hours}h
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Adjustment Form */}
                        <div className="border-t border-border pt-4">
                            <p className="text-sm font-medium mb-3">{t('employees.manual_adjustment')}</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('employees.flex_hours_label')}</Label>
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={flexAdjustment.hours}
                                        onChange={(e) => setFlexAdjustment({ ...flexAdjustment, hours: e.target.value })}
                                        placeholder={t('employees.flex_hours_placeholder')}
                                        data-testid="flex-hours-input"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">{t('employees.flex_hours_hint')}</p>
                                </div>
                            </div>
                            <div className="mt-3">
                                <Label className="label-uppercase mb-2 block">{t('employees.flex_reason_label')}</Label>
                                <Textarea
                                    value={flexAdjustment.reason}
                                    onChange={(e) => setFlexAdjustment({ ...flexAdjustment, reason: e.target.value })}
                                    placeholder={t('employees.flex_reason_placeholder')}
                                    rows={2}
                                    data-testid="flex-reason-input"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFlexDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleFlexAdjustment} disabled={saving} data-testid="adjust-flex-btn">
                            {saving ? t('employees.adjusting') : t('employees.apply_adjustment')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
